import { appConfirm } from "@/lib/appDialog";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  FileSignature,
  Loader2,
  RefreshCw,
  Save,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const KINDS = [
  {
    value: "ASSOCIATION_AGREEMENT",
    label: "اتفاقية الجمعية",
    description: "الاتفاقية الثنائية بين الجمعية ومنصة سياق.",
  },
  {
    value: "STUDENT_COMMITMENT",
    label: "وثيقة التزام التدريب التعاوني",
    description: "التعهد الذي ينشأ تلقائيًا بعد قبول طلب الطالب.",
  },
];

const stateLabel = {
  CANONICAL: "النص الأساسي",
  DRAFT: "مسودة",
  PUBLISHED: "منشور",
  RETIRED: "متقاعد",
};

function formatDate(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("ar-SA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export default function ContractTemplatesPanel() {
  const [kind, setKind] = useState(KINDS[0].value);
  const [data, setData] = useState(null);
  const [title, setTitle] = useState("");
  const [legalText, setLegalText] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [dirty, setDirty] = useState(false);

  const source = useMemo(
    () => data?.draft || data?.published || data?.canonical,
    [data],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setPreview(null);
    try {
      const response = await api.get(
        `/contracts/admin/templates/${kind}`,
      );
      const result = response.data;
      const current =
        result.draft || result.published || result.canonical;
      setData(result);
      setTitle(current?.title || "");
      setLegalText(current?.legal_text || "");
      setDirty(false);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    load();
  }, [load]);

  const updateTitle = (value) => {
    setTitle(value);
    setDirty(true);
    setPreview(null);
  };

  const updateText = (value) => {
    setLegalText(value);
    setDirty(true);
    setPreview(null);
  };

  const saveDraft = async () => {
    if (!title.trim() || !legalText.trim()) {
      toast.error("عنوان العقد ونصه مطلوبان");
      return;
    }
    setBusy("save");
    try {
      const payload = {
        title: title.trim(),
        legal_text: legalText,
      };
      if (data?.draft) {
        payload.expected_revision = data.draft.revision;
      }
      const response = await api.put(
        `/contracts/admin/templates/${kind}/draft`,
        payload,
      );
      setData((current) => ({
        ...current,
        draft: response.data.template,
      }));
      setDirty(false);
      setPreview(null);
      toast.success("حُفظت مسودة العقد");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(null);
    }
  };

  const createPreview = async () => {
    setBusy("preview");
    try {
      const response = await api.post(
        `/contracts/admin/templates/${kind}/preview`,
        {
          title: title.trim(),
          legal_text: legalText,
        },
      );
      setPreview(response.data);
      toast.success("تم تحديث المعاينة");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    if (!data?.draft) {
      toast.error("احفظ مسودة أولًا قبل النشر");
      return;
    }
    if (dirty) {
      toast.error("لديك تغييرات غير محفوظة؛ احفظها أولًا");
      return;
    }
    if (!preview || preview.content_hash !== data.draft.content_hash) {
      toast.error("عاين النسخة المحفوظة قبل نشرها");
      return;
    }
    const confirmed = await appConfirm(
      "سيصبح هذا الإصدار ثابتًا وغير قابل للتعديل، وستتقاعد النسخة المنشورة السابقة. هل تريد المتابعة؟",
    );
    if (!confirmed) return;

    setBusy("publish");
    try {
      await api.post(
        `/contracts/admin/templates/${kind}/publish`,
        {
          expected_revision: data.draft.revision,
          confirm_content_hash: data.draft.content_hash,
        },
      );
      toast.success("تم نشر إصدار العقد");
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(null);
    }
  };

  const restorePublished = () => {
    const fallback = data?.published || data?.canonical;
    if (!fallback) return;
    setTitle(fallback.title || "");
    setLegalText(fallback.legal_text || "");
    setDirty(true);
    setPreview(null);
    toast.info("أُعيد النص داخل المحرر؛ اضغط حفظ لإنشاء المسودة");
  };

  const selected = KINDS.find((item) => item.value === kind);

  if (loading) {
    return (
      <div className="grid min-h-72 place-items-center" data-testid="contracts-loading">
        <Loader2 className="h-8 w-8 animate-spin text-teal-700" />
      </div>
    );
  }

  return (
    <section className="space-y-5" data-testid="contract-templates-panel">
      <div className="rounded-2xl border border-teal-100 bg-teal-50/70 p-4">
        <div className="flex items-start gap-3">
          <FileSignature className="mt-0.5 h-5 w-5 shrink-0 text-teal-700" />
          <div>
            <h2 className="font-heading font-bold text-slate-900">
              قوالب العقود الداخلية
            </h2>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              التعديل متاح للمسودة فقط. عند النشر تُجمّد النسخة وبصمتها،
              وأي تعديل لاحق يبدأ إصدارًا جديدًا.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {KINDS.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => setKind(item.value)}
            className={`rounded-2xl border p-4 text-right transition ${
              kind === item.value
                ? "border-teal-600 bg-teal-50 ring-2 ring-teal-100"
                : "border-slate-200 bg-white hover:border-slate-300"
            }`}
            data-testid={`contract-kind-${item.value}`}
          >
            <div className="font-bold text-slate-900">{item.label}</div>
            <div className="mt-1 text-xs leading-5 text-slate-500">
              {item.description}
            </div>
          </button>
        ))}
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(380px,.8fr)]">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-heading text-lg font-bold text-slate-900">
                تحرير {selected?.label}
              </h3>
              <div className="mt-1 flex flex-wrap gap-2 text-xs">
                <StatusBadge state={source?.state} />
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">
                  الإصدار {source?.version ?? 0}
                </span>
                {data?.draft && (
                  <span className="rounded-full bg-amber-50 px-2.5 py-1 text-amber-800">
                    مراجعة {data.draft.revision}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={restorePublished}
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-300 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="contract-restore"
            >
              <RefreshCw className="h-4 w-4" />
              استعادة المنشور
            </button>
          </div>

          <label className="mt-5 block text-sm font-semibold text-slate-700">
            عنوان العقد
            <input
              value={title}
              onChange={(event) => updateTitle(event.target.value)}
              className="mt-2 min-h-11 w-full rounded-xl border border-slate-300 px-3 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
              data-testid="contract-title"
            />
          </label>

          <label className="mt-4 block text-sm font-semibold text-slate-700">
            بنود العقد
            <textarea
              value={legalText}
              onChange={(event) => updateText(event.target.value)}
              rows={24}
              spellCheck={false}
              className="mt-2 w-full resize-y rounded-xl border border-slate-300 p-4 font-mono text-sm leading-7 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
              data-testid="contract-legal-text"
            />
          </label>

          <div className="mt-3">
            <div className="text-xs font-semibold text-slate-500">
              المتغيرات المحمية
            </div>
            <div className="mt-2 flex flex-wrap gap-2" dir="ltr">
              {(source?.variables || []).map((variable) => (
                <code
                  key={variable}
                  className="rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-700"
                >
                  {`{{${variable}}}`}
                </code>
              ))}
            </div>
          </div>

          {dirty && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              توجد تغييرات غير محفوظة ولن تدخل في العقود حتى تحفظ المسودة وتنشرها.
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={saveDraft}
              disabled={Boolean(busy)}
              className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-teal-700 px-4 font-bold text-white hover:bg-teal-600 disabled:opacity-60"
              data-testid="contract-save"
            >
              {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              حفظ المسودة
            </button>
            <button
              type="button"
              onClick={createPreview}
              disabled={Boolean(busy)}
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-300 px-4 font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              data-testid="contract-preview"
            >
              {busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
              تحديث المعاينة
            </button>
            <button
              type="button"
              onClick={publish}
              disabled={Boolean(busy) || !data?.draft}
              className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-slate-900 px-4 font-bold text-white hover:bg-slate-800 disabled:opacity-50"
              data-testid="contract-publish"
            >
              {busy === "publish" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              نشر الإصدار
            </button>
          </div>
        </div>

        <aside className="xl:sticky xl:top-5 xl:self-start">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div className="flex items-center gap-2 font-bold text-slate-900">
                <Eye className="h-4 w-4 text-teal-700" />
                معاينة العقد
              </div>
              {preview && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  محدثة
                </span>
              )}
            </div>
            <div
              className="max-h-[72vh] min-h-72 overflow-y-auto whitespace-pre-wrap p-5 text-sm leading-8 text-slate-700"
              data-testid="contract-preview-content"
            >
              {preview?.rendered_text || (
                <div className="grid min-h-60 place-items-center text-center text-slate-400">
                  اضغط «تحديث المعاينة» لعرض العقد بعد تعبئة متغيراته التجريبية.
                </div>
              )}
            </div>
          </div>

          {data?.published && (
            <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs leading-6 text-emerald-900">
              الإصدار المنشور: {data.published.version} — نُشر في{" "}
              {formatDate(data.published.published_at)}
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}

function StatusBadge({ state }) {
  const published = state === "PUBLISHED";
  return (
    <span
      className={`rounded-full px-2.5 py-1 font-semibold ${
        published
          ? "bg-emerald-50 text-emerald-700"
          : "bg-amber-50 text-amber-800"
      }`}
    >
      {stateLabel[state] || state || "غير معروف"}
    </span>
  );
}
